import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { shuffle, times } from 'lodash-es'
import Slider from 'react-slick'
import Spacer from '../../lib/spacer'
import { getReviews } from './reviews'
import { useWindowSize } from '../../lib/hooks'
import { useMediaQuery } from 'react-responsive'
import { NextButton, PreviousButton } from './arrow_button'
import { ClientOnly, useIsHydrated, useSlickA11yFix } from '../../util/ssr'
import { useLang } from '../../lib/i18n'
import { FaStar, FaChevronCircleLeft, FaChevronCircleRight } from 'react-icons/fa'

const sliderProps = {
    infinite: true,
    adaptiveHeight: false,
    variableWidth: true,
    speed: 500,
    slidesToScroll: 1,
    arrows: false,
    dots: false
}

const Wrapper = styled.div`
    overflow: hidden; /** IE bug */
    position: relative;
`

const Card = styled.div`
    background: white;
    border-radius: 8px;
    display: flex;
    padding: 30px;
    margin-top: 50px;
    margin-bottom: 50px;
    flex-direction: column;
    transition: transform 0.3s ease, box-shadow 0.3s ease;

    &:hover {
        transform: translateY(-4px);
        box-shadow: 0px 16px 40px rgba(0, 0, 0, 0.25);
    }

    ${({ windowWidth, isSmall }) => {
        if (isSmall) {
            return css`
                width: ${windowWidth - 60}px;
                margin-left: 30px;
            `
        }

        return css`
            width: 400px;
            margin-left: 50px;
        `
    }}

    .card-header {
        display: flex;
        justify-content: space-between;
    }

    .reviewer-name {
        color: ${(props) => props.theme.palette.greyScale.grey};
        font-size: 1rem;
        font-weight: bold;
    }
`

const StarsWrapper = styled.div`
    display: flex;
`

const Star = styled.div`
    &:not(:last-child) {
        margin-right: 1px;
    }

    color: ${(props) => props.theme.palette.brand.orange};
`

const Stars = () => (
    <StarsWrapper>
        {times(5, (i) => (
            <Star key={i}>
                <FaStar />
            </Star>
        ))}
    </StarsWrapper>
)

export default () => {
    const sliderRef = useRef()
    const isHydrated = useIsHydrated()
    const slickA11yRef = useSlickA11yFix()
    const lang = useLang()
    const reviews = getReviews(lang)
    const [randomisedReviews, setRandomisedReviews] = useState(reviews)
    const [windowWidth] = useWindowSize()
    const isSmall = useMediaQuery({ maxWidth: 609 })

    useEffect(() => {
        setRandomisedReviews(shuffle([...reviews]))
    }, [lang])

    const staticFallback = (() => {
        const review = randomisedReviews[0]
        return (
            <Wrapper>
                <Spacer size={50} />
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <Card
                        windowWidth={windowWidth || 400}
                        isSmall={isSmall}
                        className="box-shadow"
                    >
                        <div className="card-header">
                            <span className="reviewer-name">{review.name}</span>
                            <Stars />
                        </div>
                        <Spacer size={10} />
                        <p>{review.review}</p>
                    </Card>
                </div>
                {/* The carousel only exists after hydration, so without this the
                    prerendered page carries one review instead of all of them. */}
                <div className="show-for-sr">
                    {randomisedReviews.slice(1).map((other) => (
                        <blockquote key={other.name}>
                            <p>{other.review}</p>
                            <cite>{other.name}</cite>
                        </blockquote>
                    ))}
                </div>
            </Wrapper>
        )
    })()

    return (
        <ClientOnly fallback={staticFallback}>
            <Wrapper ref={slickA11yRef}>
                <Spacer size={50} />
                <PreviousButton aria-label="Previous review" onClick={() => sliderRef.current.slickPrev()}>
                    <FaChevronCircleLeft className="arrow-icon box-shadow" />
                </PreviousButton>
                <Slider ref={sliderRef} {...sliderProps}>
                    {randomisedReviews.map((review, i) => (
                        <div key={i}>
                            <Card
                                key={i}
                                windowWidth={windowWidth}
                                isSmall={isSmall}
                                className="box-shadow"
                            >
                                <div className="card-header">
                                    <span className="reviewer-name">{review.name}</span>
                                    <Stars />
                                </div>
                                <Spacer size={10} />
                                <p>{review.review}</p>
                            </Card>
                        </div>
                    ))}
                </Slider>
                <NextButton aria-label="Next review" onClick={() => sliderRef.current.slickNext()}>
                    <FaChevronCircleRight className="arrow-icon box-shadow" />
                </NextButton>
            </Wrapper>
        </ClientOnly>
    )
}
